import React, { useState, useEffect } from 'react';
import { 
  User, 
  ExternalLink, 
  Plus, 
  Calendar, 
  Clock, 
  FileText, 
  CheckCircle, 
  AlertCircle, 
  XCircle,
  MessageSquare,
  Eye,
  X,
  Download,
  Target,
  Edit
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet, apiPut, convertEpochToLocalTime, convertEpochToLocalDate, formatTimeRange, calculateDuration } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { getTaskById } from '../../config/taskRegistry';
import { Button, Card, Badge, Input, Select, Spinner, Alert, Divider } from '../design-system';
import CreateTaskModal from './CreateTaskModal';
import { MoodHistoryModal } from '../mood-tracker';
import GroundingAssignment from '../grounding/GroundingAssignment';
import { PatientGroundingAnalytics } from '../grounding/GroundingAnalytics';
import TherapeuticGoals from '../therapeutic-goals/TherapeuticGoals';
import therapeuticGoalsService from '../../services/therapeuticGoalsService';
import { jsPDF } from 'jspdf';
import moodTrackerService from '../../services/moodTrackerService';
import groundingService from '../../services/groundingService';
import { MOOD_OPTIONS } from '../../utils/moodTrackerUtils';
import { TaskGriefJournalingConfig } from '../tasks/TaskGriefJournaling';
import NotesSection from '../notes/NotesSection';

const PatientDetail = ({ patient, onPatientUpdate }) => {
  const { userInfo, user, getTherapistInfo } = useAuth();
  const [patientData, setPatientData] = useState(patient);
  const [tasks, setTasks] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [timelineFilter, setTimelineFilter] = useState('all'); // all, tasks, appointments
  const [timelineData, setTimelineData] = useState([]);
  const [summaryModal, setSummaryModal] = useState({ isOpen: false, task: null, patientName: '' });
  const [showMoodHistoryModal, setShowMoodHistoryModal] = useState(false);
  const [goalsMap, setGoalsMap] = useState({});
  const [showEditNotebookModal, setShowEditNotebookModal] = useState(false);
  const [notebookLinkValue, setNotebookLinkValue] = useState('');

  // Filter options for dropdown
  const filterOptions = [
    { value: 'all', label: 'All', count: timelineData.length },
    { value: 'tasks', label: 'Tasks', count: tasks.length },
    { value: 'appointments', label: 'Appointments', count: appointments.length }
  ];

  const getCurrentFilterLabel = () => {
    const option = filterOptions.find(opt => opt.value === timelineFilter);
    return option ? `${option.label} (${option.count})` : 'All';
  };


  const renderCompletionData = (completionData) => {
    if (!completionData) return null;

    // Handle different types of completion data
    if (completionData.type === 'grief' && completionData.questions) {
      return (
        <div className="mt-3 p-3 bg-gray-50 rounded-md">
          <h5 className="text-sm font-medium text-gray-700 mb-2">Journaling Summary</h5>
          <div className="space-y-2">
            <div className="text-xs text-gray-600">
              Completed {completionData.completedQuestions} of {completionData.totalQuestions} questions
            </div>
            {completionData.questions.slice(0, 3).map((q, index) => (
              <div key={index} className="text-sm">
                <div className="font-medium text-gray-700">{q.text}</div>
                <div className="text-gray-600 mt-1 break-words">
                  {q.answer ? `"${q.answer}"` : 'No answer provided'}
                </div>
              </div>
            ))}
            {completionData.questions.length > 3 && (
              <div className="text-xs text-gray-500 italic">
                ... and {completionData.questions.length - 3} more questions
              </div>
            )}
          </div>
        </div>
      );
    }

    // Handle onboarding completion data
    if (completionData.type === 'onboarding' && completionData.summary) {
      return (
        <div className="mt-3 p-3 bg-gray-50 rounded-md">
          <h5 className="text-sm font-medium text-gray-700 mb-2">Onboarding Summary</h5>
          <div className="text-sm text-gray-600 break-words">
            {completionData.summary}
          </div>
        </div>
      );
    }

    // Handle custom task completion notes
    if (completionData.completionNotes) {
      return (
        <div className="mt-3 p-3 bg-gray-50 rounded-md">
          <h5 className="text-sm font-medium text-gray-700 mb-2">Completion Notes</h5>
          <div className="text-sm text-gray-600 break-words">
            {completionData.completionNotes}
          </div>
        </div>
      );
    }

    // Generic completion data
    if (typeof completionData === 'object') {
      return (
        <div className="mt-3 p-3 bg-gray-50 rounded-md">
          <h5 className="text-sm font-medium text-gray-700 mb-2">Completion Summary</h5>
          <div className="text-sm text-gray-600 break-words">
            {JSON.stringify(completionData, null, 2)}
          </div>
        </div>
      );
    }

    return null;
  };

  useEffect(() => {
    if (patient) {
      setPatientData(patient);
      fetchPatientData();
      fetchGoals();
    }
  }, [patient]);

  // Sync notebook link value when modal opens
  useEffect(() => {
    if (showEditNotebookModal) {
      setNotebookLinkValue(patientData.notebookLink || '');
    }
  }, [showEditNotebookModal, patientData.notebookLink]);

  const fetchGoals = async () => {
    if (!patient?.patientId || !user) return;
    
    try {
      const response = await therapeuticGoalsService.getGoals(user, patient.patientId);
      const goals = response.goals || [];
      // Create a map of goalId -> goal for quick lookup
      const map = {};
      goals.forEach(goal => {
        map[goal.goalId] = goal;
      });
      setGoalsMap(map);
    } catch (error) {
      console.error('Error fetching goals for task display:', error);
      // Silently fail - goals are optional
    }
  };


  const fetchPatientData = async () => {
    try {
      setLoading(true);
      
      // Fetch tasks and appointments in parallel
      const [tasksResponse, appointmentsResponse] = await Promise.all([
        apiGet(`/.netlify/functions/get-patient-tasks?patientId=${patient.patientId}`, userInfo?.userId),
        apiGet(`/.netlify/functions/get-bookings?patientId=${patient.patientId}&filter=all`, userInfo?.userId)
      ]);

      const fetchedTasks = tasksResponse.tasks || [];
      const fetchedAppointments = appointmentsResponse.bookings || [];

      setTasks(fetchedTasks);
      setAppointments(fetchedAppointments);
      
      // Create timeline data
      createTimelineData(fetchedTasks, fetchedAppointments);
    } catch (error) {
      console.error('Error fetching patient data:', error);
      toast.error('Failed to load patient data');
    } finally {
      setLoading(false);
    }
  };

  const createTimelineData = (tasksData, appointmentsData) => {
    const timeline = [];

    // Add tasks to timeline
    tasksData.forEach(task => {
      const taskDate = task.createdAt ? new Date(task.createdAt) : new Date();
      timeline.push({
        id: `task-${task._id}`,
        type: 'task',
        data: task,
        timestamp: taskDate.getTime(),
        date: taskDate
      });
    });

    // Add appointments to timeline
    appointmentsData.forEach(appointment => {
      timeline.push({
        id: `appointment-${appointment._id}`,
        type: 'appointment',
        data: appointment,
        timestamp: appointment.startEpoch ? appointment.startEpoch * 1000 : Date.now(),
        date: appointment.startEpoch ? new Date(appointment.startEpoch * 1000) : new Date()
      });
    });

    // Sort by timestamp (most recent first)
    timeline.sort((a, b) => b.timestamp - a.timestamp);

    setTimelineData(timeline);
  };

  const getTaskStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4" style={{color: 'var(--color-success)'}} />;
      case 'pending':
        return <Clock className="h-4 w-4" style={{color: 'var(--color-accent)'}} />;
      case 'overdue':
        return <AlertCircle className="h-4 w-4" style={{color: 'var(--color-error)'}} />;
      default:
        return <Clock className="h-4 w-4" style={{color: 'var(--color-text)', opacity: 0.6}} />;
    }
  };

  const getTaskStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-white';
      case 'pending':
        return 'text-gray-900';
      case 'overdue':
        return 'text-white';
      default:
        return 'text-gray-900';
    }
  };

  const getAppointmentStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'text-white';
      case 'pending':
        return 'text-gray-900';
      case 'cancelled':
        return 'text-white';
      default:
        return 'text-gray-900';
    }
  };

  const formatDateGroup = (date) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (dateOnly.getTime() === today.getTime()) {
      return 'Today';
    } else if (dateOnly.getTime() === yesterday.getTime()) {
      return 'Yesterday';
    } else {
      const diffTime = today.getTime() - dateOnly.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays <= 7) {
        return `${diffDays} days ago`;
      } else {
        return date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
        });
      }
    }
  };

  const filteredTimelineData = timelineData.filter(item => {
    if (timelineFilter === 'all') return true;
    if (timelineFilter === 'tasks') return item.type === 'task';
    if (timelineFilter === 'appointments') return item.type === 'appointment';
    return true;
  });

  const groupedTimelineData = filteredTimelineData.reduce((groups, item) => {
    const dateKey = item.date.toDateString();
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(item);
    return groups;
  }, {});

  const handleTaskCreated = () => {
    fetchPatientData();
    fetchGoals(); // Refresh goals to show in newly created tasks
    setShowTaskModal(false);
  };

  const handleUpdateNotebookLink = async () => {
    try {
      const response = await apiPut(
        '/.netlify/functions/update-patient',
        {
          patientId: patientData.patientId,
          notebookLink: notebookLinkValue.trim() || null
        },
        userInfo?.userId
      );

      // Update local patient data
      setPatientData(prev => ({
        ...prev,
        notebookLink: response.patient.notebookLink
      }));

      // If onPatientUpdate callback exists, call it
      if (onPatientUpdate) {
        onPatientUpdate({
          ...patientData,
          notebookLink: response.patient.notebookLink
        });
      }

      toast.success('Notebook link updated successfully');
      setShowEditNotebookModal(false);
    } catch (error) {
      console.error('Error updating notebook link:', error);
      toast.error(error.message || 'Failed to update notebook link');
    }
  };

  const handleShowSummaryModal = (task) => {
    setSummaryModal({
      isOpen: true,
      task: task,
      patientName: patientData?.name || 'Patient'
    });
  };

  const handleCloseSummaryModal = () => {
    setSummaryModal({ isOpen: false, task: null, patientName: '' });
  };

  const generatePDF = async () => {
    try {
      if (!user) {
        toast.error('Please log in to generate PDF');
        return;
      }

      toast.loading('Generating PDF...', { id: 'pdf-generation' });
      
      // Fetch additional data needed for PDF
      let moodHistory = [];
      let groundingAssignment = null;
      let groundingAnalytics = null;
      let therapistInfo = null;
      
      try {
        // Fetch mood history
        moodHistory = await moodTrackerService.getPatientMoodHistory(
          patientData.patientId,
          'all',
          user
        );
      } catch (error) {
        console.error('Error fetching mood history for PDF:', error);
      }

      try {
        // Fetch grounding assignment
        const assignmentResponse = await groundingService.getAssignment(user, patientData.patientId);
        groundingAssignment = assignmentResponse.assignment;
        
        // Fetch grounding analytics if assignment exists
        if (groundingAssignment) {
          try {
            const analyticsResponse = await groundingService.getAnalytics(user, patientData.patientId, 'all');
            groundingAnalytics = analyticsResponse.analytics;
          } catch (error) {
            console.error('Error fetching grounding analytics for PDF:', error);
          }
        }
      } catch (error) {
        console.error('Error fetching grounding assignment for PDF:', error);
      }

      try {
        // Fetch therapist information - use therapistId from patientData or current userInfo
        const therapistId = patientData.therapistId || userInfo?.userId;
        if (therapistId && getTherapistInfo) {
          therapistInfo = await getTherapistInfo(therapistId);
          
          // Also try to get more details from patient info endpoint
          try {
            const patientInfoResponse = await apiGet(`/.netlify/functions/get-patient-info?patientId=${patientData.patientId}`, userInfo?.userId);
            if (patientInfoResponse.therapistInfo) {
              // Merge with additional fields like specialization and phone
              therapistInfo = {
                ...therapistInfo,
                specialization: patientInfoResponse.therapistInfo.specialization,
                phone: patientInfoResponse.therapistInfo.phone
              };
            }
          } catch (error) {
            console.error('Error fetching additional therapist info:', error);
            // Continue with basic therapist info
          }
        }
      } catch (error) {
        console.error('Error fetching therapist info for PDF:', error);
      }

      // Create PDF
      const doc = new jsPDF();
      let yPosition = 20;
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      const maxWidth = pageWidth - (margin * 2);
      const lineHeight = 7;

      // Helper function to add text with word wrapping
      const addText = (text, x, y, maxWidth, fontSize = 11) => {
        doc.setFontSize(fontSize);
        const lines = doc.splitTextToSize(text, maxWidth);
        doc.text(lines, x, y);
        return lines.length * lineHeight;
      };

      // Helper function to add a section header
      const addSectionHeader = (title, y) => {
        // Check if we need a new page - leave enough space for header and a bit of content
        if (y > doc.internal.pageSize.getHeight() - 50) {
          doc.addPage();
          y = 20;
        }
        doc.setFontSize(16);
        doc.setFont(undefined, 'bold');
        doc.text(title, margin, y);
        y += lineHeight + 5;
        
        // Draw a line under the header
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.5);
        doc.line(margin, y - 2, pageWidth - margin, y - 2);
        y += 5;
        
        return y;
      };

      // Helper function to add a subsection
      const addSubsection = (title, y) => {
        if (y > doc.internal.pageSize.getHeight() - 30) {
          doc.addPage();
          y = 20;
        }
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(title, margin, y);
        y += lineHeight + 3;
        return y;
      };

      // Helper function to convert hex color to RGB
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        } : { r: 100, g: 100, b: 100 };
      };

      // Helper function to generate task summary text from completion data
      const generateTaskSummary = (task, completionData) => {
        if (!completionData) return null;

        const taskConfig = getTaskById(task.taskTypeId);
        const lines = [];

        // Grief Journaling tasks
        if (task.taskTypeId && ['task_003', 'task_004', 'task_005', 'task_006', 'task_007'].includes(task.taskTypeId)) {
          const questions = TaskGriefJournalingConfig[task.taskTypeId]?.questions || [];
          if (completionData.answers && Object.keys(completionData.answers).length > 0) {
            lines.push('Grief Journaling Responses:');
            questions.forEach(question => {
              const answer = completionData.answers[question.id];
              if (answer) {
                lines.push(`Q: ${question.text}`);
                lines.push(`A: ${answer}`);
                lines.push(''); // Empty line between Q&A
              }
            });
          }
        }
        // Reality Check tasks
        else if (task.taskTypeId === 'task_008' || taskConfig?.name?.toLowerCase().includes('reality check')) {
          if (completionData.fearedOutcome) {
            lines.push(`Feared Outcome: ${completionData.fearedOutcome}`);
          }
          if (completionData.supportingFacts && completionData.supportingFacts.length > 0) {
            lines.push('Supporting Facts:');
            completionData.supportingFacts.forEach(fact => {
              lines.push(`  • ${fact}`);
            });
          }
          if (completionData.challengingFacts && completionData.challengingFacts.length > 0) {
            lines.push('Challenging Facts:');
            completionData.challengingFacts.forEach(fact => {
              lines.push(`  • ${fact}`);
            });
          }
          if (completionData.certaintyReduction !== undefined) {
            lines.push(`Certainty Reduction: ${completionData.certaintyReduction > 0 ? `-${completionData.certaintyReduction}%` : '0%'}`);
          }
          if (completionData.initialCertainty !== undefined) {
            lines.push(`Initial Certainty: ${completionData.initialCertainty}%`);
          }
          if (completionData.finalCertainty !== undefined) {
            lines.push(`Final Certainty: ${completionData.finalCertainty}%`);
          }
        }
        // Custom tasks or tasks with completion notes
        else if (completionData.completionNotes) {
          lines.push('Completion Notes:');
          lines.push(completionData.completionNotes);
        }
        // Tasks with summary
        else if (completionData.summary) {
          lines.push('Summary:');
          lines.push(completionData.summary);
        }
        // Grief task with questions array (legacy format)
        else if (completionData.type === 'grief' && completionData.questions) {
          lines.push(`Completed ${completionData.completedQuestions || 0} of ${completionData.totalQuestions || 0} questions`);
          if (completionData.questions && completionData.questions.length > 0) {
            completionData.questions.slice(0, 5).forEach(q => {
              lines.push(`Q: ${q.text}`);
              lines.push(`A: ${q.answer || 'No answer provided'}`);
              lines.push('');
            });
            if (completionData.questions.length > 5) {
              lines.push(`... and ${completionData.questions.length - 5} more questions`);
            }
          }
        }
        // Generic completion data (fallback)
        else if (typeof completionData === 'object') {
          lines.push('Completion Details:');
          Object.entries(completionData).forEach(([key, value]) => {
            if (value !== null && value !== undefined && key !== 'type') {
              if (Array.isArray(value)) {
                lines.push(`${key}: ${value.join(', ')}`);
              } else if (typeof value === 'object') {
                lines.push(`${key}: ${JSON.stringify(value)}`);
              } else {
                lines.push(`${key}: ${value}`);
              }
            }
          });
        }

        return lines.length > 0 ? lines.join('\n') : null;
      };

      // Title
      doc.setFontSize(20);
      doc.setFont(undefined, 'bold');
      doc.text('Patient Information Report', margin, yPosition);
      yPosition += lineHeight + 5;

      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Generated on: ${new Date().toLocaleString()}`, margin, yPosition);
      yPosition += lineHeight + 10;

      // Patient Information Section
      yPosition = addSectionHeader('Patient Information', yPosition);
      
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      yPosition += addText(`Name: ${patientData.name || 'N/A'}`, margin, yPosition, maxWidth);
      yPosition += addText(`Patient ID: ${patientData.patientId || 'N/A'}`, margin, yPosition, maxWidth);
      
      if (patientData.notebookLink) {
        yPosition += addText(`Notebook Link: ${patientData.notebookLink}`, margin, yPosition, maxWidth);
      }
      
      yPosition += 10;

      // Therapist Information Section
      yPosition = addSectionHeader('Therapist Information', yPosition);
      
      if (therapistInfo) {
        doc.setFontSize(11);
        doc.setFont(undefined, 'normal');
        yPosition += addText(`Name: ${therapistInfo.name || 'N/A'}`, margin, yPosition, maxWidth);
        yPosition += addText(`Email: ${therapistInfo.email || 'N/A'}`, margin, yPosition, maxWidth);
        
        if (therapistInfo.specialization) {
          yPosition += addText(`Specialization: ${therapistInfo.specialization}`, margin, yPosition, maxWidth);
        }
        
        if (therapistInfo.phone) {
          yPosition += addText(`Phone: ${therapistInfo.phone}`, margin, yPosition, maxWidth);
        }
        
        if (therapistInfo.userId) {
          yPosition += addText(`Therapist ID: ${therapistInfo.userId}`, margin, yPosition, maxWidth);
        }
      } else {
        yPosition += addText('Therapist information not available', margin, yPosition, maxWidth);
      }
      
      yPosition += 10;

      // Grounding Assignment Section
      yPosition = addSectionHeader('Grounding Assignment', yPosition);
      
      if (groundingAssignment) {
        yPosition = addSubsection('Current Assignment', yPosition);
        
        const techniqueType = groundingAssignment.techniqueType === 'breathing' ? 'Breathing Exercise' : 'Thoughts and Facts';
        yPosition += addText(`Technique Type: ${techniqueType}`, margin, yPosition, maxWidth);
        
        if (groundingAssignment.techniqueType === 'breathing' && groundingAssignment.techniqueConfig) {
          const config = groundingAssignment.techniqueConfig;
          yPosition += addText(`Cycles: ${config.cycles || 'N/A'}`, margin, yPosition, maxWidth);
          yPosition += addText(`Inhale: ${config.inhaleDuration || 'N/A'}s, Hold: ${config.holdDuration || 'N/A'}s, Exhale: ${config.exhaleDuration || 'N/A'}s`, margin, yPosition, maxWidth);
          
          if (config.instructions) {
            yPosition += addText(`Instructions: ${config.instructions}`, margin, yPosition, maxWidth);
          }
        }

        if (groundingAnalytics) {
          yPosition += 5;
          yPosition = addSubsection('Analytics', yPosition);
          yPosition += addText(`Total Sessions: ${groundingAnalytics.totalSessions || 0}`, margin, yPosition, maxWidth);
          
          if (groundingAnalytics.totalDuration) {
            const minutes = Math.floor(groundingAnalytics.totalDuration / 60);
            const seconds = groundingAnalytics.totalDuration % 60;
            yPosition += addText(`Total Duration: ${minutes}m ${seconds}s`, margin, yPosition, maxWidth);
          }
          
          if (groundingAnalytics.avgDuration) {
            const avgMinutes = Math.floor(groundingAnalytics.avgDuration / 60);
            const avgSeconds = Math.floor(groundingAnalytics.avgDuration % 60);
            yPosition += addText(`Average Duration: ${avgMinutes}m ${avgSeconds}s`, margin, yPosition, maxWidth);
          }
        }
      } else {
        yPosition += addText('No grounding assignment assigned', margin, yPosition, maxWidth);
      }
      
      yPosition += 10;

      // Mood History Section
      yPosition = addSectionHeader('Mood History', yPosition);
      
      if (moodHistory && moodHistory.length > 0) {
        yPosition += addText(`Total Entries: ${moodHistory.length}`, margin, yPosition, maxWidth);
        yPosition += 10;

        // Create mood chart for entire duration
        if (moodHistory.length > 0) {
          // Check if we need a new page before starting chart
          if (yPosition > doc.internal.pageSize.getHeight() - 120) {
            doc.addPage();
            yPosition = 20;
          }
          
          yPosition = addSubsection('Mood Trend Chart (All Time)', yPosition);
          
          // Process mood data for chart
          const sortedMoods = [...moodHistory].sort((a, b) => {
            const dateA = new Date(a.date || a.createdAt || a.timestamp).getTime();
            const dateB = new Date(b.date || b.createdAt || b.timestamp).getTime();
            return dateA - dateB;
          });

          // Chart dimensions - ensure everything fits within margins
          const leftLabelWidth = 35; // Space for Y-axis labels
          const chartStartX = margin + leftLabelWidth;
          const chartStartY = yPosition + 5;
          const chartWidth = pageWidth - (margin * 2) - leftLabelWidth - 5; // Ensure right margin
          const chartHeight = 80;
          const chartEndX = chartStartX + chartWidth;
          const chartEndY = chartStartY + chartHeight;
          
          // Chart padding (reduced to fit better)
          const chartPadding = 10;
          const plotStartX = chartStartX + chartPadding;
          const plotStartY = chartStartY + chartPadding;
          const plotWidth = chartWidth - (chartPadding * 2);
          const plotHeight = chartHeight - (chartPadding * 2);
          const plotEndX = plotStartX + plotWidth;
          const plotEndY = plotStartY + plotHeight;

          // Find min/max dates
          const dates = sortedMoods.map(m => {
            const date = m.date || m.createdAt || m.timestamp;
            return new Date(date).getTime();
          });
          const minDate = Math.min(...dates);
          const maxDate = Math.max(...dates);
          const dateRange = maxDate - minDate || 1;

          // Convert mood to numeric value (1-5)
          const moodToValue = (moodId) => {
            const index = MOOD_OPTIONS.findIndex(m => m.id === moodId);
            return index >= 0 ? index + 1 : 3; // Default to middle if not found
          };

          // Draw chart border
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(0.5);
          doc.rect(chartStartX, chartStartY, chartWidth, chartHeight);

          // Draw Y-axis labels (mood levels)
          const moodLabels = MOOD_OPTIONS.map((mood, index) => {
            const yValue = index + 1;
            const yPos = plotEndY - ((yValue - 1) / 4) * plotHeight;
            return { label: mood.label, yPos, emoji: mood.emoji, color: mood.color };
          });

          moodLabels.forEach((label, index) => {
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            // Ensure labels stay within left margin - skip emoji, use text label only
            doc.text(label.label, margin + 5, label.yPos + 2);
          });

          // Draw X-axis (time)
          doc.setDrawColor(200, 200, 200);
          doc.line(plotStartX, plotEndY, plotEndX, plotEndY);
          
          // Add X-axis labels (first, middle, last dates) - ensure they fit
          if (sortedMoods.length > 0) {
            const firstDate = new Date(sortedMoods[0].date || sortedMoods[0].createdAt || sortedMoods[0].timestamp);
            const lastDate = new Date(sortedMoods[sortedMoods.length - 1].date || sortedMoods[sortedMoods.length - 1].createdAt || sortedMoods[sortedMoods.length - 1].timestamp);
            const midIndex = Math.floor(sortedMoods.length / 2);
            const midDate = new Date(sortedMoods[midIndex].date || sortedMoods[midIndex].createdAt || sortedMoods[midIndex].timestamp);

            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            const dateStr1 = firstDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            doc.text(dateStr1, plotStartX, plotEndY + 8);
            if (sortedMoods.length > 1) {
              const dateStr2 = midDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              const dateStr3 = lastDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              // Center middle label
              doc.text(dateStr2, plotStartX + (plotWidth / 2) - 15, plotEndY + 8);
              // Position last label to fit within page margin (estimate ~30px width for date string)
              const estimatedLabelWidth = 30;
              const lastLabelX = Math.min(plotEndX - estimatedLabelWidth, pageWidth - margin - estimatedLabelWidth);
              doc.text(dateStr3, lastLabelX, plotEndY + 8);
            }
          }

          // Draw grid lines for mood levels
          doc.setDrawColor(240, 240, 240);
          doc.setLineWidth(0.3);
          for (let i = 1; i <= 5; i++) {
            const yPos = plotEndY - ((i - 1) / 4) * plotHeight;
            doc.line(plotStartX, yPos, plotEndX, yPos);
          }

          // Plot mood data points and line
          if (sortedMoods.length > 0) {
            const points = [];
            
            sortedMoods.forEach((mood, index) => {
              const date = new Date(mood.date || mood.createdAt || mood.timestamp).getTime();
              const xPos = plotStartX + ((date - minDate) / dateRange) * plotWidth;
              const moodValue = moodToValue(mood.mood);
              const yPos = plotEndY - ((moodValue - 1) / 4) * plotHeight;
              
              points.push({ x: xPos, y: yPos, moodValue, moodOption: MOOD_OPTIONS.find(m => m.id === mood.mood) });
            });

            // Draw line connecting points
            if (points.length > 1) {
              doc.setDrawColor(44, 166, 164); // Primary color
              doc.setLineWidth(1);
              for (let i = 0; i < points.length - 1; i++) {
                doc.line(points[i].x, points[i].y, points[i + 1].x, points[i + 1].y);
              }
            }

            // Draw points
            points.forEach((point) => {
              const color = point.moodOption?.color || '#666';
              const rgb = hexToRgb(color);
              doc.setFillColor(rgb.r, rgb.g, rgb.b);
              doc.setDrawColor(255, 255, 255);
              doc.setLineWidth(0.5);
              doc.circle(point.x, point.y, 2, 'FD'); // Fill and draw
            });
          }

          // Update position after chart, accounting for labels below chart (reduce spacing)
          yPosition = chartEndY + 12;
        }
        
        // Check if we need a new page before showing detailed entries
        if (yPosition > doc.internal.pageSize.getHeight() - 50) {
          doc.addPage();
          yPosition = 20;
        }
        
        // Show recent mood entries (last 20)
        const recentMoods = moodHistory.slice(0, 20);
        yPosition = addSubsection('Recent Mood Entries (Latest 20)', yPosition);
        
        recentMoods.forEach((entry, index) => {
          if (yPosition > doc.internal.pageSize.getHeight() - 30) {
            doc.addPage();
            yPosition = 20;
          }
          
          const moodOption = MOOD_OPTIONS.find(m => m.id === entry.mood);
          // Use text label only, skip emoji (jsPDF doesn't support emojis properly)
          const moodLabel = moodOption ? moodOption.label : entry.mood;
          
          const date = entry.date || entry.createdAt || entry.timestamp;
          const dateStr = date ? new Date(date).toLocaleDateString() : 'Unknown date';
          
          yPosition += addText(`${dateStr}: ${moodLabel}`, margin + 5, yPosition, maxWidth - 5, 10);
          
          if (entry.comment) {
            yPosition += addText(`  Comment: ${entry.comment}`, margin + 10, yPosition, maxWidth - 10, 9);
          }
          
          yPosition += 3;
        });
        
        if (moodHistory.length > 20) {
          yPosition += addText(`... and ${moodHistory.length - 20} more entries`, margin, yPosition, maxWidth, 10);
        }
      } else {
        yPosition += addText('No mood history entries found', margin, yPosition, maxWidth);
      }
      
      yPosition += 10;

      // Tasks Section
      yPosition = addSectionHeader('Tasks', yPosition);
      
      if (tasks && tasks.length > 0) {
        yPosition += addText(`Total Tasks: ${tasks.length}`, margin, yPosition, maxWidth);
        yPosition += 5;
        
        tasks.forEach((task, index) => {
          if (yPosition > doc.internal.pageSize.getHeight() - 40) {
            doc.addPage();
            yPosition = 20;
          }
          
          const taskConfig = getTaskById(task.taskTypeId);
          const taskTitle = taskConfig?.name || task.title || task.taskType || 'Untitled Task';
          
          yPosition = addSubsection(`Task ${index + 1}: ${taskTitle}`, yPosition);
          
          yPosition += addText(`Status: ${task.status || 'N/A'}`, margin + 5, yPosition, maxWidth - 5, 10);
          
          if (task.description) {
            yPosition += addText(`Description: ${task.description}`, margin + 5, yPosition, maxWidth - 5, 10);
          }
          
          if (task.createdAt) {
            yPosition += addText(`Created: ${new Date(task.createdAt).toLocaleDateString()}`, margin + 5, yPosition, maxWidth - 5, 10);
          }
          
          if (task.dueDate) {
            yPosition += addText(`Due: ${new Date(task.dueDate).toLocaleDateString()}`, margin + 5, yPosition, maxWidth - 5, 10);
          }
          
          if (task.completedAt) {
            yPosition += addText(`Completed: ${new Date(task.completedAt).toLocaleDateString()}`, margin + 5, yPosition, maxWidth - 5, 10);
          }
          
          if (task.status === 'completed' && task.completionData) {
            yPosition += 3;
            const taskSummary = generateTaskSummary(task, task.completionData);
            
            if (taskSummary) {
              yPosition = addSubsection('Task Summary', yPosition);
              yPosition += addText(taskSummary, margin + 5, yPosition, maxWidth - 5, 9);
            } else {
              // Fallback to basic completion data if summary generation fails
              yPosition += addText('Completion Data:', margin + 5, yPosition, maxWidth - 5, 10);
              
              if (task.completionData.type === 'grief' && task.completionData.questions) {
                yPosition += addText(`  Completed ${task.completionData.completedQuestions || 0} of ${task.completionData.totalQuestions || 0} questions`, margin + 10, yPosition, maxWidth - 10, 9);
              } else if (task.completionData.summary) {
                yPosition += addText(`  Summary: ${task.completionData.summary}`, margin + 10, yPosition, maxWidth - 10, 9);
              } else if (task.completionData.completionNotes) {
                yPosition += addText(`  Notes: ${task.completionData.completionNotes}`, margin + 10, yPosition, maxWidth - 10, 9);
              }
            }
          }
          
          yPosition += 5;
        });
      } else {
        yPosition += addText('No tasks found', margin, yPosition, maxWidth);
      }
      
      yPosition += 10;

      // Appointments Section
      yPosition = addSectionHeader('Appointments', yPosition);
      
      if (appointments && appointments.length > 0) {
        yPosition += addText(`Total Appointments: ${appointments.length}`, margin, yPosition, maxWidth);
        yPosition += 5;
        
        appointments.forEach((appointment, index) => {
          if (yPosition > doc.internal.pageSize.getHeight() - 40) {
            doc.addPage();
            yPosition = 20;
          }
          
          yPosition = addSubsection(`Appointment ${index + 1}`, yPosition);
          
          yPosition += addText(`Status: ${appointment.status || 'N/A'}`, margin + 5, yPosition, maxWidth - 5, 10);
          
          if (appointment.startEpoch) {
            yPosition += addText(`Date: ${convertEpochToLocalDate(appointment.startEpoch)}`, margin + 5, yPosition, maxWidth - 5, 10);
            yPosition += addText(`Time: ${formatTimeRange(appointment.startEpoch, appointment.endEpoch)}`, margin + 5, yPosition, maxWidth - 5, 10);
            yPosition += addText(`Duration: ${calculateDuration(appointment.startEpoch, appointment.endEpoch)}`, margin + 5, yPosition, maxWidth - 5, 10);
          }
          
          if (appointment.message) {
            yPosition += addText(`Message: ${appointment.message}`, margin + 5, yPosition, maxWidth - 5, 10);
          }
          
          yPosition += 5;
        });
      } else {
        yPosition += addText('No appointments found', margin, yPosition, maxWidth);
      }

      // Save PDF
      // Sanitize patient name for filename (remove special characters)
      const sanitizedName = (patientData.name || 'Report').replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const fileName = `Patient_${sanitizedName}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      toast.success('PDF generated successfully!', { id: 'pdf-generation' });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF. Please try again.', { id: 'pdf-generation' });
    }
  };

  const renderTaskItem = (task) => {
    const hasCompletionData = task.status === 'completed' && task.completionData;
    
    // Get task metadata from registry
    const taskConfig = getTaskById(task.taskTypeId);

    return (
      <Card variant="default" padding="medium" className="hover:shadow-md transition-shadow">
        <div className="flex items-start space-x-3 md:space-x-4">
          <div className="flex-shrink-0 mt-1">
            <div className="h-5 w-5 md:h-6 md:w-6 flex items-center justify-center">
              {getTaskStatusIcon(task.status)}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
              {/* Left Column - Task Info */}
              <div className="lg:col-span-2 space-y-2 md:space-y-3">
                {/* Row 1: Title and Description */}
                <div>
                  <h4 className="text-sm md:text-base font-medium text-gray-900 break-words">
                    {taskConfig?.name || task.title || task.taskType || 'Untitled Task'}
                  </h4>
                  <p className="text-sm md:text-base text-gray-600 mt-1 md:mt-2 break-words">
                    {taskConfig?.description || task.description || 'No description available'}
                  </p>
                  {task.goalId && goalsMap[task.goalId] && (
                    <div className="flex items-center gap-2 mt-2 text-xs md:text-sm">
                      <Target className="h-3 w-3 md:h-4 md:w-4" style={{color: 'var(--color-primary)'}} />
                      <span className="text-gray-600">
                        Helps achieve: <span className="font-medium text-gray-900">{goalsMap[task.goalId].title}</span>
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Row 2: Dates - Better layout for laptop */}
                <div className="flex flex-col md:flex-row md:flex-wrap gap-1 md:gap-3 text-xs md:text-sm text-gray-500">
                  <span className="flex items-center">
                    <span className="font-medium mr-1">Created:</span>
                    {task.createdAt ? new Date(task.createdAt).toLocaleDateString() : 'Unknown'}
                  </span>
                  {task.dueDate && (
                    <span className="flex items-center">
                      <span className="font-medium mr-1">Due:</span>
                      {new Date(task.dueDate).toLocaleDateString()}
                    </span>
                  )}
                  {task.completedAt && (
                    <span className="flex items-center">
                      <span className="font-medium mr-1">Completed:</span>
                      {new Date(task.completedAt).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>

              {/* Right Column - Status and Summary */}
              <div className="flex flex-col items-start lg:items-end justify-start lg:justify-start gap-2 md:gap-3">
                <Badge 
                  variant={task.status === 'completed' ? 'success' : task.status === 'pending' ? 'accent' : task.status === 'overdue' ? 'error' : 'default'}
                  size="small"
                  className="text-xs md:text-sm"
                >
                  {task.status}
                </Badge>
                {hasCompletionData && (
                  <Button
                    onClick={() => handleShowSummaryModal(task)}
                    variant="ghost"
                    size="small"
                    className="text-xs md:text-sm"
                    title="View summary"
                  >
                    <Eye className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                    Summary
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  const renderAppointmentItem = (appointment) => (
    <Card variant="default" padding="medium" className="hover:shadow-md transition-shadow">
      <div className="flex items-start space-x-3 md:space-x-4">
        <div className="flex-shrink-0 mt-1">
          <div className="h-5 w-5 md:h-6 md:w-6 flex items-center justify-center">
            <Calendar className="h-4 w-4 md:h-5 md:w-5" style={{color: 'var(--color-primary)'}} />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 md:gap-4">
            <h4 className="text-sm md:text-base font-medium text-gray-900 flex-1 min-w-0">
              Appointment
            </h4>
            <Badge 
              variant={appointment.status === 'confirmed' ? 'success' : appointment.status === 'pending' ? 'accent' : appointment.status === 'cancelled' ? 'error' : 'default'}
              size="small"
              className="flex-shrink-0 text-xs md:text-sm"
            >
              {appointment.status}
            </Badge>
          </div>
          <div className="flex flex-col md:flex-row md:items-center md:space-x-4 lg:space-x-6 mt-2 md:mt-3 text-sm md:text-base text-gray-600 gap-2 md:gap-0">
            <div className="flex items-center space-x-1.5 md:space-x-2">
              <Calendar className="h-3 w-3 md:h-4 md:w-4 flex-shrink-0" />
              <span className="break-words font-medium">{convertEpochToLocalDate(appointment.startEpoch)}</span>
            </div>
            <div className="flex items-center space-x-1.5 md:space-x-2">
              <Clock className="h-3 w-3 md:h-4 md:w-4 flex-shrink-0" style={{color: 'var(--color-primary)'}} />
              <span className="break-words">{formatTimeRange(appointment.startEpoch, appointment.endEpoch)}</span>
            </div>
            <span className="break-words">{calculateDuration(appointment.startEpoch, appointment.endEpoch)}</span>
          </div>
          {appointment.message && (
            <div className="flex items-start space-x-2 md:space-x-3 mt-3 md:mt-4 text-sm md:text-base text-gray-600">
              <MessageSquare className="h-3 w-3 md:h-4 md:w-4 mt-0.5 flex-shrink-0" />
              <p className="italic break-words">"{appointment.message}"</p>
            </div>
          )}
          {appointment.meetLink && appointment.status === 'confirmed' && (
            <div className="mt-3 md:mt-4">
              <a
                href={appointment.meetLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-sm md:text-base text-indigo-600 hover:text-indigo-700 font-medium"
              >
                <ExternalLink className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                Join Google Meet
              </a>
            </div>
          )}
        </div>
      </div>
    </Card>
  );

  if (loading) {
    return (
      <div className="space-y-4 md:space-y-6">
        <div className="text-center py-8">
          <Spinner size="medium" className="mx-auto" />
          <p className="text-gray-600 mt-2">Loading patient data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 lg:space-y-8">

      {/* Patient Info Card - Enhanced for laptop */}
      <Card padding="large">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-4 md:space-x-6">
            <div className="h-12 w-12 md:h-16 md:w-16 rounded-full flex items-center justify-center flex-shrink-0" style={{backgroundColor: 'rgba(44, 166, 164, 0.1)'}}>
              <User className="h-6 w-6 md:h-8 md:w-8" style={{color: 'var(--color-primary)'}} />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg md:text-xl lg:text-2xl font-medium text-gray-900">
                {patientData.name || 'Unnamed Patient'}
              </h2>
              {patientData.notebookLink ? (
                <div className="flex items-center space-x-2 text-sm md:text-base mt-2">
                  <div className="flex items-center space-x-1" style={{color: 'var(--color-primary)'}}>
                    <ExternalLink className="h-4 w-4 md:h-5 md:w-5" />
                    <a 
                      href={patientData.notebookLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      View Notebook
                    </a>
                  </div>
                  <Button
                    onClick={() => setShowEditNotebookModal(true)}
                    variant="ghost"
                    size="small"
                    className="p-1 h-auto text-gray-400 hover:text-gray-600"
                    title="Edit notebook link"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => setShowEditNotebookModal(true)}
                  variant="outline"
                  size="small"
                  className="mt-2 flex items-center gap-1"
                >
                  <Plus className="h-4 w-4" />
                  Add Notebook Link
                </Button>
              )}
            </div>
          </div>
          <Button
            onClick={generatePDF}
            variant="outline"
            size="small"
            className="flex items-center gap-2 flex-shrink-0"
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Download PDF</span>
            <span className="sm:hidden">PDF</span>
          </Button>
        </div>
      </Card>

      {/* Therapeutic Goals - Full width on laptop */}
      <TherapeuticGoals patient={patientData} />

      {/* Notes Section */}
      <NotesSection patient={patientData} />

      {/* Two-column layout for laptop: Grounding Assignment and Mood History */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Grounding Assignment */}
        <div className="lg:col-span-1">
          <GroundingAssignment patient={patientData} />
        </div>

        {/* Mood History Tile */}
        <Card padding="large" className="h-full">
          <div className="flex items-center justify-between h-full">
            <div className="flex items-center space-x-4 md:space-x-6 flex-1">
              <div className="h-12 w-12 md:h-16 md:w-16 rounded-full flex items-center justify-center flex-shrink-0" style={{backgroundColor: 'rgba(44, 166, 164, 0.1)'}}>
                <span className="text-2xl md:text-3xl">😊</span>
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-lg md:text-xl font-medium text-gray-900">Mood History</h3>
                <p className="text-sm md:text-base text-gray-600 mt-1">Track patient's emotional journey</p>
              </div>
            </div>
            <Button
              onClick={() => setShowMoodHistoryModal(true)}
              variant="outline"
              className="flex items-center gap-2 flex-shrink-0"
            >
              <Eye className="h-4 w-4" />
              <span className="hidden sm:inline">View</span>
            </Button>
          </div>
        </Card>
      </div>

      {/* Timeline Filter - Enhanced for laptop */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 md:gap-4">
        {/* Mobile: Dropdown + Add Task button */}
        <div className="flex items-center justify-between sm:hidden">
          <div className="w-32">
            <Select
              value={timelineFilter}
              onChange={(value) => setTimelineFilter(value)}
              options={filterOptions.map(option => ({
                value: option.value,
                label: `${option.label} (${option.count})`
              }))}
              size="small"
            />
          </div>
          
          <Button
            onClick={() => setShowTaskModal(true)}
            variant="primary"
            size="small"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Task
          </Button>
        </div>

        {/* Desktop: Tabs + Add Task button */}
        <div className="hidden sm:flex sm:items-center sm:justify-between w-full">
          <div className="flex space-x-1 md:space-x-3 lg:space-x-4">
            {filterOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setTimelineFilter(option.value)}
                className={`px-4 py-2 md:px-5 md:py-2.5 text-sm md:text-base font-medium rounded-md transition-colors duration-200 flex-shrink-0 ${
                  timelineFilter === option.value
                    ? 'text-white'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
                style={timelineFilter === option.value ? {backgroundColor: 'var(--color-primary)'} : {}}
              >
                {option.label} ({option.count})
              </button>
            ))}
          </div>
          
          <Button
            onClick={() => setShowTaskModal(true)}
            variant="primary"
            size="medium"
            className="flex-shrink-0"
          >
            <Plus className="h-4 w-4 md:h-5 md:w-5 mr-1 md:mr-2" />
            <span className="hidden md:inline">Add Task</span>
            <span className="md:hidden">Add</span>
          </Button>
        </div>
      </div>

      {/* Timeline - Enhanced spacing for laptop */}
      <Card>
        <div className="px-4 md:px-6 lg:px-8 py-4 md:py-5 border-b border-gray-200">
          <h3 className="text-lg md:text-xl font-medium text-gray-900">Timeline</h3>
        </div>
        
        <div className="p-4 md:p-6 lg:p-8">
          {Object.keys(groupedTimelineData).length === 0 ? (
            <div className="text-center py-8 md:py-12">
              <FileText className="h-12 w-12 md:h-16 md:w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-base md:text-lg">No {timelineFilter === 'all' ? 'activities' : timelineFilter} found</p>
              <p className="text-sm md:text-base text-gray-400 mt-1">
                {timelineFilter === 'all' 
                  ? 'This patient has no tasks or appointments yet'
                  : `This patient has no ${timelineFilter} yet`
                }
              </p>
            </div>
          ) : (
            <div className="space-y-6 md:space-y-8">
              {Object.entries(groupedTimelineData).map(([dateKey, items], index) => (
                <div key={dateKey}>
                  {index > 0 && <Divider className="my-6 md:my-8" />}
                  <h4 className="text-sm md:text-base font-medium text-gray-700 mb-4 md:mb-5 flex items-center">
                    <Calendar className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                    {formatDateGroup(new Date(dateKey))}
                  </h4>
                  <div className="space-y-3 md:space-y-4 ml-4 md:ml-6 lg:ml-8">
                    {items.map((item) => (
                      <div key={item.id}>
                        {item.type === 'task' ? renderTaskItem(item.data) : renderAppointmentItem(item.data)}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Grounding Analytics - Merged into Grounding Assignment */}
      {/* <PatientGroundingAnalytics 
        patientId={patientData?.patientId} 
        patientName={patientData?.name || 'Patient'} 
      /> */}

      {/* Create Task Modal */}
      {showTaskModal && (
        <CreateTaskModal
          isOpen={showTaskModal}
          onClose={() => setShowTaskModal(false)}
          patients={[patientData]}
          onTaskCreated={handleTaskCreated}
        />
      )}

      {/* Summary Modal */}
      {summaryModal.isOpen && summaryModal.task && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto" variant="elevated">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Task Summary - {summaryModal.patientName}
              </h3>
              <Button
                onClick={handleCloseSummaryModal}
                variant="ghost"
                size="small"
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </Button>
            </div>
            <div className="p-6">
              {(() => {
                const taskConfig = getTaskById(summaryModal.task.taskTypeId);
                const TaskComponent = taskConfig?.component;
                
                if (TaskComponent?.ResultsSummary) {
                  return React.createElement(TaskComponent.ResultsSummary, {
                    task: summaryModal.task,
                    completionData: summaryModal.task.completionData,
                    patientName: summaryModal.patientName
                  });
                } else {
                  return (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No summary available for this task.</p>
                    </div>
                  );
                }
              })()}
            </div>
          </Card>
        </div>
      )}
      
      {/* Mood History Modal */}
      <MoodHistoryModal
        isOpen={showMoodHistoryModal}
        onClose={() => setShowMoodHistoryModal(false)}
        patientId={patientData?.patientId}
        title={`${patientData?.name || 'Patient'}'s Mood History`}
      />

      {/* Edit Notebook Link Modal */}
      {showEditNotebookModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4">
          <Card className="max-w-md w-full" variant="elevated">
            <div className="flex items-center justify-between p-4 md:p-6 border-b">
              <h2 className="text-lg md:text-xl font-semibold text-gray-900">
                {patientData.notebookLink ? 'Edit Notebook Link' : 'Add Notebook Link'}
              </h2>
              <Button
                onClick={() => setShowEditNotebookModal(false)}
                variant="ghost"
                size="small"
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X className="h-5 w-5 md:h-6 md:w-6" />
              </Button>
            </div>

            <div className="p-4 md:p-6 space-y-4">
              <Input
                type="url"
                label="Notebook Link"
                placeholder="https://notion.so/patient-notes or https://docs.google.com/..."
                helperText="Link to patient's notebook or notes (Notion, Google Docs, etc.)"
                value={notebookLinkValue}
                onChange={(e) => setNotebookLinkValue(e.target.value)}
              />

              <div className="flex space-x-3 pt-4">
                <Button
                  onClick={handleUpdateNotebookLink}
                  variant="primary"
                  size="medium"
                  className="flex-1"
                >
                  {patientData.notebookLink ? 'Update' : 'Add'} Link
                </Button>
                <Button
                  onClick={() => setShowEditNotebookModal(false)}
                  variant="outline"
                  size="medium"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PatientDetail;
